import { z } from 'zod';
import { RequestFileMimeSchema } from '../requests/request-files.js';
import { IssuedStorageObjectKeySchema } from '../storage.js';
import { CargoProblemStatusSchema, CargoProblemTerminalOutcomeSchema } from './status.js';
import {
  CARGO_PROBLEM_CARGO_CANDIDATES_MAX,
  CARGO_PROBLEM_COMMENT_MAX,
  CARGO_PROBLEM_DESCRIPTION_MAX,
  CARGO_PROBLEM_FILE_MAX_BYTES,
  CARGO_PROBLEM_SEARCH_MIN,
  CargoProblemCategorySchema,
  CargoProblemQueueSchema,
} from './vocab.js';
import { makeCargoProblemTransitionInputSchema } from './transition-input.js';
import {
  CargoProblemActorSchema,
  CargoProblemActivityItemSchema,
  CargoProblemCommentSchema,
  CargoProblemDetailSchema,
  CargoProblemFileSchema,
  CargoProblemListItemSchema,
} from './wire.js';

export const CreateCargoProblemInputSchema = z
  .object({
    cargoArticleId: z.string().uuid(),
    category: CargoProblemCategorySchema,
    queue: CargoProblemQueueSchema,
    description: z.string().trim().min(1).max(CARGO_PROBLEM_DESCRIPTION_MAX),
  })
  .strict();
export type CreateCargoProblemInput = z.infer<typeof CreateCargoProblemInputSchema>;

export const PatchCargoProblemInputSchema = z
  .object({
    category: CargoProblemCategorySchema.optional(),
    queue: CargoProblemQueueSchema.optional(),
    description: z.string().trim().min(1).max(CARGO_PROBLEM_DESCRIPTION_MAX).optional(),
  })
  .strict()
  .refine((input) => Object.keys(input).length > 0, 'at least one field is required');
export type PatchCargoProblemInput = z.infer<typeof PatchCargoProblemInputSchema>;

export const CargoProblemTransitionInputSchema = makeCargoProblemTransitionInputSchema({});
export type CargoProblemTransitionInput = z.infer<typeof CargoProblemTransitionInputSchema>;

const OptionalDateSchema = z.string().datetime().optional();
const BooleanQueryFlagSchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((value) => value === true || value === 'true');
export const CargoProblemListQuerySchema = z
  .object({
    status: CargoProblemStatusSchema.optional(),
    category: CargoProblemCategorySchema.optional(),
    queue: CargoProblemQueueSchema.optional(),
    authorId: z.string().uuid().optional(),
    assigneeId: z.string().uuid().optional(),
    carrier: z.string().min(1).optional(),
    createdFrom: OptionalDateSchema,
    createdTo: OptionalDateSchema,
    updatedFrom: OptionalDateSchema,
    updatedTo: OptionalDateSchema,
    overdueOnly: BooleanQueryFlagSchema.optional(),
    q: z.string().trim().min(CARGO_PROBLEM_SEARCH_MIN).max(200).optional(),
  })
  .strict();
export type CargoProblemListQuery = z.infer<typeof CargoProblemListQuerySchema>;

export const CargoProblemListResponseSchema = z
  .object({ items: z.array(CargoProblemListItemSchema), hasMore: z.boolean() })
  .strict();
export const CargoProblemDetailResponseSchema = z
  .object({ problem: CargoProblemDetailSchema })
  .strict();

export const CargoProblemCommentCreateInputSchema = z
  .object({ body: z.string().trim().min(1).max(CARGO_PROBLEM_COMMENT_MAX) })
  .strict();
export type CargoProblemCommentCreateInput = z.infer<typeof CargoProblemCommentCreateInputSchema>;
export const CargoProblemCommentsResponseSchema = z
  .object({ items: z.array(CargoProblemCommentSchema) })
  .strict();
export const CargoProblemActivityResponseSchema = z
  .object({ items: z.array(CargoProblemActivityItemSchema) })
  .strict();

export const CargoProblemFileBindInputSchema = z
  .object({
    objectKey: IssuedStorageObjectKeySchema,
    fileName: z.string().trim().min(1).max(255),
    contentType: RequestFileMimeSchema,
    contentLength: z.number().int().positive().max(CARGO_PROBLEM_FILE_MAX_BYTES),
  })
  .strict();
export type CargoProblemFileBindInput = z.infer<typeof CargoProblemFileBindInputSchema>;
export const CargoProblemFileResponseSchema = z.object({ file: CargoProblemFileSchema }).strict();
export const CargoProblemFileDownloadResponseSchema = z
  .object({ downloadUrl: z.string().url(), expiresAt: z.string().datetime() })
  .strict();

const counterSchema = <T extends z.ZodTypeAny>(value: T) =>
  z.object({ value, count: z.number().int().nonnegative() }).strict();
export const CargoProblemSummarySchema = z
  .object({
    byStatus: z.array(counterSchema(CargoProblemStatusSchema)),
    byCategory: z.array(counterSchema(CargoProblemCategorySchema)),
    byQueue: z.array(counterSchema(CargoProblemQueueSchema)),
    byOutcome: z.array(counterSchema(CargoProblemTerminalOutcomeSchema)),
  })
  .strict();
export const CargoProblemFilterOptionsSchema = z
  .object({
    authors: z.array(CargoProblemActorSchema),
    assignees: z.array(CargoProblemActorSchema),
    carriers: z.array(z.string()),
  })
  .strict();
export const CargoProblemActionableCountSchema = z
  .object({ count: z.number().int().nonnegative() })
  .strict();

export const CargoProblemCargoCandidatesQuerySchema = z
  .object({
    q: z
      .string()
      .trim()
      .max(200)
      .refine((value) => value.length === 0 || value.length >= CARGO_PROBLEM_SEARCH_MIN)
      .default(''),
  })
  .strict();
export const CargoProblemCargoCandidateSchema = z
  .object({
    id: z.string().uuid(),
    article: z.string(),
    nameGoods: z.string().nullable(),
    transportationTariff: z.string().nullable(),
    weight: z.string().nullable(),
    volume: z.string().nullable(),
    carrier: z.string().nullable(),
  })
  .strict();
export const CargoProblemCargoCandidatesResponseSchema = z
  .object({
    items: z.array(CargoProblemCargoCandidateSchema).max(CARGO_PROBLEM_CARGO_CANDIDATES_MAX),
  })
  .strict();
